import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/auth';
import { api, type Ajuste, type Institucion, type UsuarioAdmin } from '@/lib/api';

interface ToggleProps { value: boolean; onChange: () => void }
function Toggle({ value, onChange }: ToggleProps) {
  return (
    <button onClick={onChange} style={{ width: 42, height: 24, borderRadius: 12, background: value ? '#1D4ED8' : 'var(--border-inner, rgba(10,22,40,0.15))', position: 'relative', transition: 'background 200ms', flexShrink: 0, border: '1px solid var(--border-inner)' }}>
      <div style={{ position: 'absolute', top: 3, left: value ? 20 : 3, width: 16, height: 16, borderRadius: '50%', background: value ? '#fff' : 'rgba(128,128,128,0.6)', boxShadow: '0 1px 4px rgba(0,0,0,0.2)', transition: 'left 200ms, background 200ms' }} />
    </button>
  );
}

const AJUSTE_FIELDS: Array<{ key: keyof Ajuste; label: string; description: string; icon: string }> = [
  { key: 'nocturno', label: 'Modo oscuro', description: 'Activa el tema oscuro en toda la interfaz', icon: 'ti-moon' },
  { key: 'edif3d', label: 'Edificios 3D', description: 'Renderiza edificios en perspectiva 3D en el mapa', icon: 'ti-building-skyscraper' },
  { key: 'limiteParroquia', label: 'Límites parroquiales', description: 'Muestra los bordes de cada parroquia', icon: 'ti-vector' },
  { key: 'alertaVial', label: 'Alertas viales', description: 'Notificaciones de incidencias viales críticas', icon: 'ti-alert-triangle' },
  { key: 'nuevosRegistros', label: 'Nuevos registros', description: 'Notificar al recibir un nuevo hallazgo en campo', icon: 'ti-bell' },
];

const ROLES = [
  { value: 'admin', label: 'Administrador', color: '#dc2626' },
  { value: 'coordinador', label: 'Coordinador', color: '#2563eb' },
  { value: 'brigadista', label: 'Brigadista', color: '#16a34a' },
  { value: 'admin_vivienda', label: 'Admin Vivienda', color: '#9333ea' },
  { value: 'sistematizador_vivienda', label: 'Sistematizador', color: '#ea580c' },
  { value: 'operador_drone', label: 'Operador Drone', color: '#0891b2' },
];

const cardStyle: React.CSSProperties = {
  background: 'var(--bg-card)',
  backdropFilter: 'blur(14px)',
  WebkitBackdropFilter: 'blur(14px)',
  borderRadius: 18,
  border: '1px solid var(--border-card)',
  boxShadow: '0 4px 16px rgba(30,58,138,0.07)',
  overflow: 'hidden',
};
const sectionTitle: React.CSSProperties = { fontSize: 11, fontWeight: 700, color: '#1D4ED8', letterSpacing: '0.06em', textTransform: 'uppercase' };
const inputSt: React.CSSProperties = { width: '100%', padding: '9px 12px', borderRadius: 9, border: '1px solid var(--input-border)', background: 'var(--input-bg)', color: 'var(--input-color)', fontSize: 13, outline: 'none', boxSizing: 'border-box' };
const labelSt: React.CSSProperties = { fontSize: 10.5, fontWeight: 700, color: 'var(--text-2)', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.06em' };

function UsersModal({ onClose, token, instituciones }: { onClose: () => void; token: string; instituciones: Institucion[] }) {
  const { user } = useAuthStore();
  const qc = useQueryClient();
  const { data: usuarios = [] } = useQuery<UsuarioAdmin[]>({ queryKey: ['usuarios'], queryFn: () => api.usuarios(token) });
  const [editUser, setEditUser] = useState<UsuarioAdmin | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ nombre: '', email: '', password: '', rol: 'brigadista', institucionId: '' as string | null });
  const [formError, setFormError] = useState('');

  const crearMut = useMutation({
    mutationFn: (body: typeof form) => api.crearUsuario(token, { ...body, institucionId: body.institucionId || null }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['usuarios'] }); setShowForm(false); setForm({ nombre: '', email: '', password: '', rol: 'brigadista', institucionId: '' }); setFormError(''); },
    onError: (e: Error) => setFormError(e.message),
  });

  const editMut = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) => api.patchUsuario(token, id, body as any),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['usuarios'] }); setEditUser(null); setFormError(''); },
    onError: (e: Error) => setFormError(e.message),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => api.eliminarUsuario(token, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['usuarios'] }),
  });

  function openEdit(u: UsuarioAdmin) {
    setEditUser(u); setShowForm(false);
    setForm({ nombre: u.nombre, email: u.email, password: '', rol: u.rol, institucionId: u.institucionId ?? '' });
    setFormError('');
  }

  function submitEdit() {
    if (!editUser) return;
    const body: Record<string, unknown> = { nombre: form.nombre, email: form.email, rol: form.rol, institucionId: form.institucionId || null };
    if (form.password) body.password = form.password;
    editMut.mutate({ id: editUser.id, body });
  }

  return (
    <div onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, zIndex: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(5,10,25,0.7)', backdropFilter: 'blur(8px)' }}>
      <div style={{ width: 560, maxWidth: 'calc(100vw - 32px)', maxHeight: '88vh', display: 'flex', flexDirection: 'column', borderRadius: 22, overflow: 'hidden', boxShadow: '0 32px 72px rgba(0,0,0,0.55)', border: '1px solid var(--border-card)', background: 'var(--bg-card-solid)', animation: 'popIn 200ms ease-out' }}>
        {/* Header */}
        <div style={{ padding: '18px 22px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid var(--border-inner)', flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--text-1)' }}>Gestión de usuarios</div>
            <div style={{ fontSize: 11, color: 'var(--text-2)', marginTop: 2 }}>{usuarios.length} usuario{usuarios.length !== 1 ? 's' : ''} registrado{usuarios.length !== 1 ? 's' : ''}</div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button onClick={() => { setShowForm(!showForm); setEditUser(null); setForm({ nombre: '', email: '', password: '', rol: 'brigadista', institucionId: '' }); setFormError(''); }}
              style={{ padding: '7px 14px', borderRadius: 10, background: showForm ? 'rgba(220,38,38,0.1)' : '#1D4ED8', color: showForm ? '#dc2626' : '#fff', fontSize: 12, fontWeight: 700 }}>
              {showForm ? 'Cancelar' : '+ Nuevo'}
            </button>
            <button onClick={onClose} style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--border-inner)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-2)', fontSize: 16 }}>
              <i className="ti ti-x" />
            </button>
          </div>
        </div>

        <div style={{ overflowY: 'auto', flex: 1 }}>
          {/* Create / Edit form */}
          {(showForm || editUser) && (
            <div style={{ padding: '16px 22px', borderBottom: '1px solid var(--border-inner)', background: 'rgba(29,78,216,0.04)', animation: 'popIn 180ms ease-out' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                <div><div style={labelSt}>Nombre *</div><input value={form.nombre} onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))} style={inputSt} placeholder="Juan Pérez" /></div>
                <div><div style={labelSt}>Email *</div><input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} style={inputSt} placeholder="user@email.com" /></div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                <div>
                  <div style={labelSt}>{editUser ? 'Nueva contraseña (vacío = sin cambio)' : 'Contraseña *'}</div>
                  <input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} style={inputSt} placeholder="Mínimo 8 caracteres" />
                </div>
                <div>
                  <div style={labelSt}>Rol *</div>
                  <select value={form.rol} onChange={e => setForm(f => ({ ...f, rol: e.target.value }))} style={{ ...inputSt, cursor: 'pointer' }}>
                    {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ marginBottom: 12 }}>
                <div style={labelSt}>Institución / Vista predeterminada</div>
                <select value={form.institucionId ?? ''} onChange={e => setForm(f => ({ ...f, institucionId: e.target.value || null }))} style={{ ...inputSt, cursor: 'pointer' }}>
                  <option value="">Nacional (todos)</option>
                  {instituciones.map(i => <option key={i.id} value={i.id}>{i.nombre}</option>)}
                </select>
                <div style={{ fontSize: 9.5, color: 'var(--text-3)', marginTop: 4 }}>Define qué datos ve al iniciar sesión</div>
              </div>
              {formError && <div style={{ fontSize: 11, color: '#dc2626', background: 'rgba(220,38,38,0.06)', padding: '6px 10px', borderRadius: 8, marginBottom: 10 }}>{formError}</div>}
              <button
                onClick={() => editUser ? submitEdit() : crearMut.mutate(form)}
                disabled={!form.nombre || !form.email || (!editUser && form.password.length < 8)}
                style={{ padding: '10px 20px', borderRadius: 10, background: (!form.nombre || !form.email || (!editUser && form.password.length < 8)) ? 'rgba(29,78,216,0.3)' : '#1D4ED8', color: '#fff', fontSize: 12, fontWeight: 700, width: '100%' }}>
                {editUser ? (editMut.isPending ? 'Guardando…' : 'Guardar cambios') : (crearMut.isPending ? 'Creando…' : 'Crear usuario')}
              </button>
            </div>
          )}

          {/* Users list */}
          {usuarios.map((u, i) => {
            const rolInfo = ROLES.find(r => r.value === u.rol);
            const isEditing = editUser?.id === u.id;
            return (
              <div key={u.id} style={{ padding: '13px 22px', borderBottom: i < usuarios.length - 1 ? '1px solid var(--border-inner)' : 'none', display: 'flex', alignItems: 'center', gap: 12, background: isEditing ? 'rgba(29,78,216,0.04)' : 'transparent' }}>
                <div style={{ width: 36, height: 36, borderRadius: '50%', background: rolInfo?.color ?? '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, color: '#fff', flexShrink: 0 }}>
                  {u.nombre.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.nombre}</div>
                  <div style={{ fontSize: 10.5, color: 'var(--text-2)' }}>{u.email}</div>
                </div>
                <span style={{ fontSize: 9.5, fontWeight: 700, padding: '3px 8px', borderRadius: 20, background: `${rolInfo?.color ?? '#64748b'}20`, color: rolInfo?.color ?? '#64748b', whiteSpace: 'nowrap' }}>{rolInfo?.label ?? u.rol}</span>
                <span style={{ fontSize: 9.5, color: 'var(--text-3)', whiteSpace: 'nowrap', display: window.innerWidth > 420 ? 'block' : 'none' }}>{u.institucion?.nombre ?? 'Nacional'}</span>
                {u.id !== user?.id && (
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button onClick={() => openEdit(u)} style={{ width: 30, height: 30, borderRadius: 8, background: isEditing ? '#1D4ED8' : 'rgba(29,78,216,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, color: isEditing ? '#fff' : '#1D4ED8' }}><i className="ti ti-edit" /></button>
                    <button onClick={() => { if (confirm(`¿Eliminar a ${u.nombre}?`)) deleteMut.mutate(u.id); }} style={{ width: 30, height: 30, borderRadius: 8, background: 'rgba(220,38,38,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, color: '#dc2626' }}><i className="ti ti-trash" /></button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function Ajustes() {
  const { token, user, institucion } = useAuthStore();
  const qc = useQueryClient();
  const isAdmin = user?.rol === 'admin';
  const [showUsers, setShowUsers] = useState(false);

  const { data: ajustes } = useQuery<Ajuste>({ queryKey: ['ajustes'], queryFn: () => api.ajustes(token!), enabled: !!token });
  const { data: instituciones = [] } = useQuery<Institucion[]>({ queryKey: ['instituciones'], queryFn: () => api.instituciones(token!), enabled: !!token && isAdmin });

  const patchAjuste = useMutation({ mutationFn: (data: Partial<Ajuste>) => api.patchAjustes(token!, data), onSuccess: () => qc.invalidateQueries({ queryKey: ['ajustes'] }) });
  function toggle(key: keyof Ajuste) { if (ajustes) patchAjuste.mutate({ [key]: !ajustes[key] }); }

  return (
    <div style={{ position: 'absolute', inset: 0, overflowY: 'auto', paddingTop: 90, paddingBottom: 90 }}>
      <div style={{ padding: '16px 16px 0', maxWidth: 620, margin: '0 auto' }}>
        <h2 style={{ fontSize: 20, fontWeight: 800, color: 'var(--text-1)', margin: '0 0 4px' }}>Ajustes</h2>
        <p style={{ fontSize: 12.5, color: 'var(--text-2)', margin: '0 0 24px' }}>Configura tu experiencia en ComuniData</p>

        {/* User card */}
        <div style={{ ...cardStyle, padding: 20, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ width: 48, height: 48, borderRadius: '50%', background: '#1D4ED8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 800, color: '#fff', flexShrink: 0 }}>
            {(user?.nombre ?? 'U').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
          </div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-1)' }}>{user?.nombre}</div>
            <div style={{ fontSize: 11, color: 'var(--text-2)', marginTop: 2 }}>{user?.email}</div>
            <div style={{ fontSize: 10, color: '#1D4ED8', fontWeight: 600, marginTop: 3 }}>
              {institucion?.nombre ?? 'Sin institución'} · <span style={{ textTransform: 'capitalize' }}>{user?.rol?.replace(/_/g, ' ')}</span>
            </div>
          </div>
        </div>

        {/* Preferences card */}
        <div style={{ ...cardStyle, marginBottom: 16 }}>
          <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border-inner)' }}>
            <div style={sectionTitle}>Preferencias</div>
          </div>
          {AJUSTE_FIELDS.map((f, i) => (
            <div key={f.key} style={{ padding: '14px 20px', borderBottom: i < AJUSTE_FIELDS.length - 1 ? '1px solid var(--border-inner)' : 'none', display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 34, height: 34, borderRadius: 10, background: ajustes?.[f.key] ? 'rgba(29,78,216,0.15)' : 'rgba(29,78,216,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'background 200ms' }}>
                <i className={`ti ${f.icon}`} style={{ fontSize: 16, color: ajustes?.[f.key] ? '#1D4ED8' : 'var(--text-2)' }} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-1)' }}>{f.label}</div>
                <div style={{ fontSize: 10.5, color: 'var(--text-2)', marginTop: 2 }}>{f.description}</div>
              </div>
              <Toggle value={ajustes?.[f.key] ?? false} onChange={() => toggle(f.key)} />
            </div>
          ))}
        </div>

        {/* Admin: Users button */}
        {isAdmin && (
          <div style={{ ...cardStyle, marginBottom: 16 }}>
            <button
              onClick={() => setShowUsers(true)}
              style={{ width: '100%', padding: '18px 20px', display: 'flex', alignItems: 'center', gap: 14, textAlign: 'left' }}>
              <div style={{ width: 42, height: 42, borderRadius: 12, background: 'rgba(29,78,216,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <i className="ti ti-users" style={{ fontSize: 18, color: '#1D4ED8' }} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-1)' }}>Gestión de usuarios</div>
                <div style={{ fontSize: 10.5, color: 'var(--text-2)', marginTop: 2 }}>Crear, editar y eliminar cuentas de usuario</div>
              </div>
              <i className="ti ti-chevron-right" style={{ fontSize: 16, color: 'var(--text-3)' }} />
            </button>
          </div>
        )}

        {/* About */}
        <div style={{ marginTop: 8, padding: '16px 20px', background: 'var(--bg-card)', backdropFilter: 'blur(10px)', borderRadius: 16, border: '1px solid var(--border-card)', textAlign: 'center', fontSize: 11, color: 'var(--text-3)' }}>
          ComuniData v2.0 · Plataforma de gestión territorial<br />
          <span style={{ color: 'var(--text-3)', opacity: 0.6 }}>© 2025 · Todos los derechos reservados</span>
        </div>
      </div>

      {showUsers && (
        <UsersModal
          onClose={() => setShowUsers(false)}
          token={token!}
          instituciones={instituciones}
        />
      )}
    </div>
  );
}
